import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Users, Heart, Eye, MousePointerClick, UserPlus, FileText, Zap, Image as ImageIcon,
  BarChart3, Target, Activity, PieChart as PieIcon, Download, Loader2, CalendarDays,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";
import api, { apiError } from "@/lib/api";
import { SafeChart } from "@/components/SafeChart";
import { toast } from "sonner";
import {
  PASTEL, NET_COLORS, NET_LABELS, PERIOD_OPTIONS, fmtN, initials, relWhen,
  AnCard, AnKpi, Donut, TopPubs, ObjectifsBars, DemoBadge, Spark, NetIcon, ACTIVITY_ICONS,
} from "./AnalyseBlocks";
import { AnalyseClientView } from "./AnalyseClient";
import { AnalyseRapports } from "./AnalyseRapports";
import { isCmClient } from "@/lib/constants";

const API = process.env.REACT_APP_BACKEND_URL;
const KPI_STYLE = [
  { icon: Users, pastel: PASTEL.blue }, { icon: Heart, pastel: PASTEL.rose },
  { icon: Eye, pastel: PASTEL.green }, { icon: MousePointerClick, pastel: PASTEL.amber },
  { icon: UserPlus, pastel: PASTEL.purple }, { icon: FileText, pastel: PASTEL.blue },
];
const EVOL_METRICS = [["audience", "Audience"], ["portee", "Portée"], ["interactions", "Interactions"], ["clics", "Clics"]];

/* ----------------------------- Vue globale ----------------------------- */
function GlobalView({ data, period, onSelectClient, onEditObjectives }) {
  const navigate = useNavigate();
  const [metric, setMetric] = useState("audience");
  const [hidden, setHidden] = useState([]);
  const series = useMemo(() => {
    const s = data.evolution?.[metric] || [];
    if (s.length <= 45) return s;
    const step = Math.ceil(s.length / 45);
    return s.filter((_, i) => i % step === 0);
  }, [data, metric]);
  const nets = (data.networks || []).map((n) => n.id);

  return (
    <div className="space-y-3" data-testid="analyse-global">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="analyse-kpis">
        {data.kpis.map((k, i) => {
          const st = KPI_STYLE[i % KPI_STYLE.length];
          return <AnKpi key={k.id} testid={`analyse-kpi-${k.id}`} icon={st.icon} pastel={st.pastel}
            value={fmtN(k.value)} label={k.label} delta={k.delta} />;
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <AnCard icon={Zap} title="Évolution de l'audience" testid="analyse-evolution" className="lg:col-span-5"
          action={
            <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="analyse-evolution-metric"
              className="ml-auto text-[11px] border border-[#E2E8F0] rounded-lg px-2 py-1 bg-white text-neutral-600">
              {EVOL_METRICS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select>
          }>
          <div className="flex flex-wrap gap-1.5 px-4 pt-2" data-testid="analyse-evolution-legend">
            {nets.map((n) => (
              <button key={n} data-testid={`analyse-evolution-toggle-${n}`}
                onClick={() => setHidden((h) => h.includes(n) ? h.filter((x) => x !== n) : [...h, n])}
                className={`flex items-center gap-1.5 text-[10px] font-medium rounded-full px-2 py-0.5 border transition-colors ${
                  hidden.includes(n) ? "opacity-40 border-neutral-200 text-neutral-400" : "border-neutral-200 text-neutral-600"}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: NET_COLORS[n] }} /> {NET_LABELS[n]}
              </button>
            ))}
          </div>
          <div className="px-2 pb-2 pt-1">
            <SafeChart height={190}>
              <LineChart data={series} margin={{ top: 8, right: 10, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false}
                  tickFormatter={(d) => { try { return format(parseISO(d), "d MMM", { locale: fr }); } catch { return d; } }} />
                <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => fmtN(v)} />
                <RTooltip formatter={(v, n) => [fmtN(v), NET_LABELS[n] || n]}
                  labelFormatter={(d) => { try { return format(parseISO(d), "d MMMM yyyy", { locale: fr }); } catch { return d; } }} />
                {nets.filter((n) => !hidden.includes(n)).map((n) => (
                  <Line key={n} type="monotone" dataKey={n} stroke={NET_COLORS[n]} strokeWidth={2} dot={false} connectNulls />
                ))}
              </LineChart>
            </SafeChart>
          </div>
        </AnCard>

        <AnCard icon={PieIcon} title="Répartition des contenus" testid="analyse-split" className="lg:col-span-3">
          {data.content_split?.length
            ? <Donut data={data.content_split} centerValue={fmtN(data.pubs_total)} centerLabel="publications" testid="analyse-split-donut" />
            : <p className="px-4 py-8 text-[13px] text-neutral-400">Aucune publication sur la période.</p>}
        </AnCard>

        <AnCard icon={BarChart3} title="Performances par réseau" testid="analyse-networks" className="lg:col-span-4">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-4 pt-2 text-[9px] font-semibold uppercase tracking-wider text-neutral-400">
            <span>Réseau</span><span className="text-right">Interactions</span>
            <span className="text-right">Clics</span><span className="text-right">Abonnés</span>
          </div>
          {(data.networks || []).map((n) => (
            <div key={n.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 items-center px-4 py-2.5 border-b border-neutral-100 last:border-0"
              data-testid={`analyse-net-${n.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <NetIcon net={n.id} />
                <div className="min-w-0">
                  <p className="text-[12px] font-bold text-[#0F172A] leading-tight">{fmtN(n.audience)}</p>
                  <p className="text-[9px] font-semibold" style={{ color: n.delta >= 0 ? "#15803D" : "#BE123C" }}>
                    {n.delta != null ? `↗ +${n.delta} %`.replace("+-", "-") : "—"}</p>
                </div>
                <Spark data={n.spark} color={NET_COLORS[n.id]} />
              </div>
              <span className="text-[11px] text-neutral-600 text-right">{fmtN(n.interactions)}</span>
              <span className="text-[11px] text-neutral-600 text-right">{fmtN(n.clicks)}</span>
              <span className="text-[11px] font-medium text-[#15803D] text-right">+ {fmtN(n.abonnes)}</span>
            </div>
          ))}
        </AnCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <AnCard icon={FileText} title="Top publications" testid="analyse-top" className="lg:col-span-5">
          <TopPubs items={data.top_publications} testid="analyse-top-list"
            onOpen={(p) => navigate(`/publications/${p.id}`)} />
        </AnCard>

        <AnCard icon={Users} title="Performance par client" testid="analyse-clients" className="lg:col-span-4">
          <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 px-4 pt-2 text-[9px] font-semibold uppercase tracking-wider text-neutral-400">
            <span>Client</span><span className="text-right">Publications</span>
            <span className="text-right">Portée</span><span className="text-right">Interactions</span><span className="text-right">Évolution</span>
          </div>
          <div className="max-h-[260px] overflow-y-auto">
            {(data.clients_perf || []).map((c) => (
              <button key={c.id} onClick={() => onSelectClient(c.id)} data-testid={`analyse-client-row-${c.id}`}
                className="w-full grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 items-center px-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <span className="flex items-center gap-2 min-w-0">
                  {c.logo
                    ? <img src={c.logo} alt="" className="w-6 h-6 rounded-full object-cover border border-neutral-100 shrink-0" />
                    : <span className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0" style={{ background: c.color }}>{initials(c.name)}</span>}
                  <span className="text-[12px] font-medium text-[#0F172A] truncate">{c.name}</span>
                </span>
                <span className="text-[11px] text-neutral-600 text-right">{c.publications}</span>
                <span className="text-[11px] text-neutral-600 text-right">{fmtN(c.reach)}</span>
                <span className="text-[11px] text-neutral-600 text-right">{fmtN(c.interactions)}</span>
                <span className={`text-[11px] font-semibold text-right ${c.delta >= 0 ? "text-[#15803D]" : "text-[#BE123C]"}`}>
                  {c.delta != null ? `↗ ${c.delta >= 0 ? "+" : ""}${c.delta} %` : "—"}</span>
              </button>
            ))}
            {!data.clients_perf?.length && <p className="px-4 py-6 text-[13px] text-neutral-400">Aucun client CM actif.</p>}
          </div>
        </AnCard>

        <AnCard icon={Target} title="Objectifs du mois" testid="analyse-objectifs" className="lg:col-span-3"
          action={onEditObjectives && (
            <button onClick={onEditObjectives} data-testid="analyse-objectifs-edit"
              className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline">Définir</button>
          )}>
          <ObjectifsBars objectives={data.objectives} testid="analyse-objectifs-list" />
        </AnCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <AnCard icon={Activity} title="Activité récente" testid="analyse-activity" className="lg:col-span-5">
          <div className="max-h-[220px] overflow-y-auto">
            {(data.activity || []).map((a) => {
              const I = ACTIVITY_ICONS[a.kind] || Activity;
              return (
                <div key={a.id} className="flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0" data-testid={`analyse-activity-${a.id}`}>
                  <span className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
                    <I className="w-3 h-3 text-neutral-500" />
                  </span>
                  <p className="flex-1 min-w-0 text-[11px] text-neutral-600 truncate">{a.text}</p>
                  <span className="text-[10px] text-neutral-400 whitespace-nowrap">{relWhen(a.when)}</span>
                </div>
              );
            })}
            {!data.activity?.length && <p className="px-4 py-6 text-[13px] text-neutral-400">Aucune activité récente.</p>}
          </div>
        </AnCard>

        <AnCard icon={PieIcon} title="Répartition des services" testid="analyse-services" className="lg:col-span-3">
          {data.services?.length
            ? <Donut data={data.services} centerValue={data.services.reduce((s, x) => s + x.value, 0)} centerLabel="clients" testid="analyse-services-donut" height={130} />
            : <p className="px-4 py-6 text-[13px] text-neutral-400">Aucun client actif.</p>}
        </AnCard>

        <AnCard icon={FileText} title="Rapports récents" testid="analyse-reports" className="lg:col-span-4"
          link="/cm?tab=analyse&sub=rapports" linkLabel="Voir tout">
          <div className="max-h-[220px] overflow-y-auto">
            {(data.reports_recent || []).map((r) => (
              <button key={r.id} onClick={() => navigate(`/cm/rapport/${r.id}`)} data-testid={`analyse-report-${r.id}`}
                className="w-full flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <FileText className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-[#0F172A] truncate">{r.client_name}</p>
                  <p className="text-[10px] text-neutral-400 capitalize truncate">{r.period_label}</p>
                </div>
                <span className={`text-[9px] font-semibold rounded-full px-2 py-0.5 border shrink-0 ${
                  r.status === "publie" ? "bg-violet-50 text-violet-700 border-violet-200"
                  : r.status === "valide" ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-neutral-100 text-neutral-500 border-neutral-200"}`}>
                  {r.status === "publie" ? "Publié" : r.status === "valide" ? "Validé" : r.status === "brouillon" ? "Brouillon" : "Généré"}
                </span>
              </button>
            ))}
            {!data.reports_recent?.length && (
              <p className="px-4 py-6 text-[13px] text-neutral-400">Aucun rapport généré — sélectionnez un client pour créer le premier.</p>)}
          </div>
        </AnCard>
      </div>
    </div>
  );
}

/* ----------------------------- Conteneur ----------------------------- */
export function CmAnalyse({ clients }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get("client") || "";
  const sub = searchParams.get("sub") || "";
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [objModal, setObjModal] = useState(false);
  const [objForm, setObjForm] = useState({ audience: "", interactions: "", abonnes: "", clics: "" });

  const cmClients = useMemo(() => clients.filter((c) => !c.archived && isCmClient(c)), [clients]);
  const setClientId = (id) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    n.set("tab", "analyse");
    if (id) n.set("client", id); else { n.delete("client"); n.delete("sub"); }
    return n;
  }, { replace: true });

  const qs = `period=${period}${period === "custom" && range.from && range.to ? `&from=${range.from}&to=${range.to}` : ""}`;
  useEffect(() => {
    if (period === "custom" && (!range.from || !range.to)) return;
    setLoading(true);
    const url = clientId ? `/analytics/client/${clientId}?${qs}` : `/analytics/global?${qs}`;
    api.get(url).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, period, range.from, range.to]);

  const exportGlobal = () => window.open(`${API}/api/analytics/global/export.csv?${qs}`, "_blank");
  const saveObjectives = async () => {
    try {
      const cm_objectives = Object.fromEntries(Object.entries(objForm).map(([k, v]) => [k, Number(v) || 0]));
      await api.patch("/settings", { cm_objectives });
      toast.success("Objectifs enregistrés");
      setObjModal(false);
      setPeriod((p) => p); // recharge via effet ci-dessous
      const r = await api.get(`/analytics/global?${qs}`);
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="cm-analyse">
      {/* Barre d'outils : client + période + export */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-4 py-2.5 flex flex-wrap items-center gap-2.5" data-testid="analyse-toolbar">
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="analyse-client-select"
          className="text-xs font-semibold border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-[#0F172A] min-w-[190px]">
          <option value="">Tous les clients</option>
          {cmClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="ml-auto flex flex-wrap items-center gap-2.5">
          {data && <DemoBadge demo={data.demo} lastSync={data.last_sync} />}
          {data?.period && (
            <span className="flex items-center gap-1.5 text-[11px] text-neutral-500 border border-[#E2E8F0] rounded-lg px-2.5 py-2" data-testid="analyse-period-label">
              <CalendarDays className="w-3.5 h-3.5" /> {data.period.label}
            </span>
          )}
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="analyse-period-select"
            className="text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-neutral-700">
            {PERIOD_OPTIONS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
          {period === "custom" && (
            <span className="flex items-center gap-1.5">
              <input type="date" value={range.from} data-testid="analyse-from"
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1.5 bg-white" />
              <input type="date" value={range.to} data-testid="analyse-to"
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1.5 bg-white" />
            </span>
          )}
          {!clientId && (
            <button onClick={exportGlobal} data-testid="analyse-export"
              className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors">
              <Download className="w-3.5 h-3.5" /> Exporter
            </button>
          )}
        </div>
      </div>

      {(loading || (data && ((clientId && !data.client) || (!clientId && !data.kpis)))) && (
        <div className="flex justify-center py-16" data-testid="analyse-loading">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-300" />
        </div>
      )}
      {!loading && data && !clientId && data.kpis && (
        sub === "rapports"
          ? <AnalyseRapports clients={cmClients} />
          : <GlobalView data={data} period={period} onSelectClient={setClientId}
              onEditObjectives={() => {
                const cur = Object.fromEntries((data.objectives || []).map((o) => [o.id, o.target || ""]));
                setObjForm({ audience: "", interactions: "", abonnes: "", clics: "", ...cur });
                setObjModal(true);
              }} />
      )}
      {!loading && data && clientId && data.client && (
        <AnalyseClientView data={data} period={period} subDefault={sub} clients={cmClients}
          onBack={() => setClientId("")} />
      )}

      {objModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setObjModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-3" data-testid="analyse-objectifs-modal"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-sm">Objectifs globaux de l'agence</h3>
            {[["audience", "Audience totale"], ["interactions", "Engagement (interactions)"],
              ["abonnes", "Nouveaux abonnés"], ["clics", "Clics site web"]].map(([k, l]) => (
              <label key={k} className="block">
                <span className="text-[11px] font-medium text-neutral-500">{l}</span>
                <input type="number" min="0" value={objForm[k]} data-testid={`analyse-obj-${k}`}
                  onChange={(e) => setObjForm((f) => ({ ...f, [k]: e.target.value }))}
                  className="mt-0.5 w-full text-sm border border-[#E2E8F0] rounded-lg px-2.5 py-2" />
              </label>
            ))}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setObjModal(false)} className="flex-1 text-xs font-medium border border-neutral-200 rounded-lg py-2 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveObjectives} data-testid="analyse-obj-save"
                className="flex-1 text-xs font-semibold bg-[#0B132B] text-white rounded-lg py-2 hover:bg-[#1C2A4F]">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
